import { checkSession } from "@features/auth";
import { authClient } from "@features/auth/authClient";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { createRootRouteWithContext, createRoute, retainSearchParams, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useRef, useState } from "react";
import { Environment, fetchQuery, graphql } from "react-relay";
import { MockEnvironment } from "relay-test-utils";
import { toast } from "sonner";
import { z } from "zod";
import { AuthRouteInvitationQuery } from "./__generated__/AuthRouteInvitationQuery.graphql";
import { AuthLayout } from "./AuthLayout";
import { ForgotPasswordPage } from "./ForgotPasswordPage";
import { InvitationHeader } from "./InvitationHeader";
import { InvitationPage } from "./InvitationPage";
import { ResetPasswordPage } from "./ResetPasswordPage";
import { SignInPage } from "./SignInPage";
import { SignUpPage } from "./SignUpPage";
import { useUserInfo } from "./useAuth";
import { VerifyEmailPage } from "./VerifyEmailPage";

const rootRoute = createRootRouteWithContext<{
	relayEnvironment: Environment | MockEnvironment;
}>()();

const searchSchema = z.object({
	redirectToPath: z.string().optional(),
	show: z.enum(["sign-in", "sign-up", "forgot-password"]).optional(),
	verifyEmail: z.string().optional(),
	token: z.string().optional(),
	invitationId: z.string().optional(),
});

const invitationQuery = graphql`
	query AuthRouteInvitationQuery($id: UUID!) {
		invitation(id: $id) {
			id
			email
			authMode
			organizationName
			...InvitationHeaderFragment
			...InvitationPageFragment
		}
	}
`;

export const AuthRoute = createRoute({
	path: "/auth",
	getParentRoute: () => rootRoute,
	component: RouteComponent,
	validateSearch: zodValidator(searchSchema),
	search: {
		middlewares: [retainSearchParams(["redirectToPath", "show", "verifyEmail", "token", "invitationId"])],
	},
	loaderDeps: ({ search: { invitationId, redirectToPath } }) => ({ invitationId, redirectToPath }),
	loader: async (ctx) => {
		const callbackUrl = new URL(location.origin + "/auth");
		if (ctx.deps.redirectToPath) {
			callbackUrl.searchParams.set("redirectToPath", ctx.deps.redirectToPath);
		}
		if (ctx.deps.invitationId) {
			callbackUrl.searchParams.set("invitationId", ctx.deps.invitationId);
		}

		return {
			...(ctx.deps.invitationId
				? await fetchQuery<AuthRouteInvitationQuery>(ctx.context.relayEnvironment, invitationQuery, {
						id: ctx.deps.invitationId,
					}).toPromise()
				: {}),
			callbackUrl: callbackUrl.toString(),
		};
	},
});

function RouteComponent() {
	const { invitation } = AuthRoute.useLoaderData();
	return (
		<>
			{invitation && <InvitationHeader invitation={invitation} />}
			<AuthLayout>
				<AuthPage />
			</AuthLayout>
		</>
	);
}

const AuthPage = () => {
	const { show, verifyEmail, token, redirectToPath, invitationId } = AuthRoute.useSearch();
	const navigate = useNavigate();
	const user = useUserInfo();
	const { invitation } = AuthRoute.useLoaderData();
	const [email, setEmail] = useState(invitation?.email ?? "");

	// Whether to auto-join the inviting organization once authenticated. Defaults to
	// true so an invitee is joined automatically after signing in / signing up. The
	// user can opt out via the checkbox to go through the normal (no-join) flow.
	const [autoJoin, setAutoJoin] = useState(true);
	// Guards against firing the accept mutation more than once per mount.
	const autoJoinAttempted = useRef(false);

	useLocalStorage({
		key: "better-auth-jwt",
		json: false,
		onUpdate: useCallback(
			(accessToken: string | undefined | null) => {
				if (accessToken && !invitationId) {
					navigate({ to: redirectToPath ?? "/" });
				}
			},
			[invitationId, navigate, redirectToPath]
		),
	});

	if (token) {
		return <ResetPasswordPage />;
	}

	if (verifyEmail) {
		return <VerifyEmailPage email={verifyEmail} />;
	}

	if (user && invitation) {
		const emailMatches = invitation.email === user.email;

		// Auto-accept the invitation and head to the dashboard when the user opted in
		// and is signed in with the invited email. Otherwise fall back to the manual
		// accept/reject page (which also handles the wrong-email case).
		if (autoJoin && emailMatches && !autoJoinAttempted.current) {
			autoJoinAttempted.current = true;
			void (async () => {
				try {
					const res = await authClient.organization.acceptInvitation({ invitationId: invitation.id });
					if (res.error) {
						throw new Error(res.error.message || "Failed to accept invitation");
					}
					await checkSession();
					navigate({ to: redirectToPath ?? "/" });
				} catch (error) {
					console.error(error);
					toast.error(error instanceof Error ? error.message : "Failed to accept invitation");
					autoJoinAttempted.current = false;
					setAutoJoin(false);
				}
			})();
			return null;
		}

		return <InvitationPage invitation={invitation} user={user} />;
	}

	const onChangeEmail = invitation ? undefined : setEmail;
	const invitationJoin = invitation
		? { organizationName: invitation.organizationName, checked: autoJoin, onCheckedChange: setAutoJoin }
		: undefined;

	switch (show ?? (invitation?.authMode === "SIGN_UP" ? "sign-up" : "sign-in")) {
		case "sign-in":
			return <SignInPage email={email} onChangeEmail={onChangeEmail} invitationJoin={invitationJoin} />;
		case "sign-up":
			return <SignUpPage email={email} onChangeEmail={onChangeEmail} invitationJoin={invitationJoin} />;
		case "forgot-password":
			return <ForgotPasswordPage email={email} onChangeEmail={onChangeEmail} />;
	}
};

export type InvitationJoinProps = {
	organizationName: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
};
